import type { ProactiveGreetingPhrase } from './proactive-greeting-api.js';
import {
  createStableWidgetMessageId,
  createWidgetMessageId,
  isWidgetMessageId,
} from '../utils/widget-message-id.js';

/** AI Assistant */
export const WIDGET_SEND_ROLE_AI = 1;
/** Visitor */
export const WIDGET_SEND_ROLE_VISITOR = 2;
/** Human Assistant / manager */
export const WIDGET_SEND_ROLE_MANAGER = 3;

export const WIDGET_SEND_HISTORY_MAX = 100;

/** Imported thread history on first `POST /api/widget/send`. */
export type WidgetSendHistoryItem = {
  role_id: typeof WIDGET_SEND_ROLE_AI | typeof WIDGET_SEND_ROLE_VISITOR | typeof WIDGET_SEND_ROLE_MANAGER;
  type: string;
  payload: string;
  datetime: string;
  /** Client id for this imported row: `wid` + UUID without hyphens. */
  message_id: string;
};

export type GreetingPhrasesToSendHistoryOptions = {
  /** Epoch ms for the first phrase; later phrases are offset by `intervalMs`. */
  baseTimeMs?: number;
  intervalMs?: number;
  /** Stable commit key — derives deterministic `message_id` per phrase. */
  greetingId?: string;
  /** Explicit ids (e.g. from offline queue) — must align with `phrases` order. */
  messageIds?: string[];
};

function resolveHistoryMessageId(
  phrase: ProactiveGreetingPhrase,
  index: number,
  options: GreetingPhrasesToSendHistoryOptions,
): string {
  const explicit = options.messageIds?.[index]?.trim();
  if (explicit && isWidgetMessageId(explicit)) return explicit;

  const payload = phrase.payload.trim();
  if (options.greetingId) {
    return createStableWidgetMessageId(
      `${options.greetingId}:${index}:${payload}`,
    );
  }

  return createWidgetMessageId();
}

/** Map proactive greeting phrases to send `history` (role_id = AI). */
export function greetingPhrasesToSendHistory(
  phrases: ProactiveGreetingPhrase[],
  options: GreetingPhrasesToSendHistoryOptions = {},
): WidgetSendHistoryItem[] {
  const base = options.baseTimeMs ?? Date.now();
  const interval = options.intervalMs ?? 1_000;

  return phrases
    .filter((phrase) => phrase.type === 'text' && phrase.payload.trim())
    .slice(0, WIDGET_SEND_HISTORY_MAX)
    .map((phrase, index) => ({
      role_id: WIDGET_SEND_ROLE_AI,
      type: 'text',
      payload: phrase.payload.trim(),
      datetime: new Date(base + index * interval).toISOString(),
      message_id: resolveHistoryMessageId(phrase, index, options),
    }));
}

/** Backfill missing ids on stored queue/history rows. */
export function ensureWidgetSendHistoryMessageIds(
  history: WidgetSendHistoryItem[],
  options: Pick<GreetingPhrasesToSendHistoryOptions, 'greetingId'> = {},
): WidgetSendHistoryItem[] {
  return history.map((item, index) => {
    const trimmed = item.message_id?.trim();
    if (trimmed && isWidgetMessageId(trimmed)) {
      return { ...item, message_id: trimmed };
    }

    const seed = options.greetingId
      ? `${options.greetingId}:${index}:${item.payload}`
      : `${item.datetime}:${index}:${item.payload}`;

    return {
      ...item,
      message_id: options.greetingId
        ? createStableWidgetMessageId(seed)
        : createWidgetMessageId(),
    };
  });
}
